import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useMemo } from 'react';
import { IngredientForm } from '@/features/ingredients/IngredientForm';
import { useIngredient, useUpdateIngredient } from '@/features/ingredients/api';
import { ingredientToFormValues, defaultFormValues } from '@/features/ingredients/formMapping';
import type { CreateIngredientInfoInput } from '@honeycomb/shared';

export const Route = createFileRoute('/_authed/ingredients/$id/edit')({
  component: EditIngredient,
});

function EditIngredient() {
  const { id } = useParams({ from: '/_authed/ingredients/$id/edit' });
  const navigate = useNavigate();
  const { data, isLoading, error } = useIngredient(id);
  const update = useUpdateIngredient(id);

  const initial = useMemo(
    () => (data ? ingredientToFormValues(data.ingredient) : defaultFormValues()),
    [data],
  );

  const handleSubmit = async (payload: CreateIngredientInfoInput) => {
    await update.mutateAsync(payload);
    await navigate({ to: '/ingredients' });
  };

  if (isLoading) {
    return <div className="p-8 text-ink-soft">Loading…</div>;
  }

  if (error || !data) {
    return (
      <div className="p-8">
        <h1 className="font-serif text-2xl mb-2">Ingredient not found</h1>
        <p className="text-ink-soft">
          {error instanceof Error ? error.message : 'Unable to load.'}
        </p>
        <button
          type="button"
          onClick={() => navigate({ to: '/ingredients' })}
          className="mt-4 text-sm text-ink-soft underline hover:text-ink"
        >
          Back to ingredients
        </button>
      </div>
    );
  }

  return (
    <div>
      <header className="px-8 pt-8 flex items-center gap-4">
        <div>
          <h1 className="font-serif text-3xl leading-tight">Edit ingredient</h1>
          <p className="text-xs text-ink-faint font-mono mt-1">{id}</p>
        </div>
        <div className="ml-auto">
          <button
            type="button"
            onClick={() => navigate({ to: '/ingredients' })}
            className="text-sm text-ink-soft hover:text-ink"
          >
            Cancel
          </button>
        </div>
      </header>
      <IngredientForm
        mode="edit"
        editingId={id}
        initialValues={initial}
        onSubmit={handleSubmit}
        isSubmitting={update.isPending}
      />
    </div>
  );
}
